import h from 'hyperscript';

import { openMenu } from 'ui/legacy-menu/flyout-menu';
import 'ui/progress-spinner/progress-spinner';

import { getLayerTemplates } from 'cadenza/api-client/layer-templates-api';
import { ApplyLayerTemplate } from 'cadenza/map/layer-actions/apply-layer-template';
import { context } from 'cadenza/map/context';
import { LayerAction } from 'cadenza/map/layer-actions/layer-action';
import { ShowLayerConfigurationPanel } from 'cadenza/map/layer-actions/show-layer-configuration-panel';
import { LayerProperty } from 'cadenza/map/layer-property';
import { icon } from 'cadenza/utils/icon/icon';
import { logger } from 'cadenza/utils/logging';
import { ActionsName } from 'cadenza/map/layer-actions/layer-actions-name';

import layerTemplatesIcon from '@disy/cadenza-icons/layer-templates.svg';

import i18n from './layer-actions.properties';
import './show-layer-configuration-menu.css';

export class ShowLayerConfigurationMenu extends LayerAction {

  #map;
  #layerId;

  constructor (layer, options) {
    super(layer, {
      label: i18n('mapconfig.showConfigurationMenuButton.label'),
      icon: icon(layerTemplatesIcon),
      ...options
    });

    this.#map = options.map;
    this.#layerId = layer.get(LayerProperty.ID);
  }

  isExpandable () {
    return true;
  }

  isSupportedSync ({ isEditingLayer }) {
    return !isEditingLayer && this.layer.get(LayerProperty.HAS_APPLICABLE_TEMPLATES);
  }

  execute (button, event) {
    const templatesContainer = h('.d-show-configuration-menu--templates-container',
      h('d-progress-spinner.d-progress-spinner-xs', { label: i18n('templates.loading.message') }));

    const content = [
      h('h3.d-show-configuration-menu--templates-header', i18n('templates')),
      templatesContainer
    ];

    // if layer is configurable, add an item at first position to open the config panel
    if (this.layer.get(LayerProperty.CONFIGURATION_CONSTRAINTS).configurable) {
      const fixedAction = createShowLayerConfigurationPanelAction(this.layer, this.#map);
      content.unshift(h('div.d-show-configuration-menu--sticky-header', actionToButton(fixedAction)));
    }

    this.active = true;

    const isShortcutAction = this.layer.get(LayerProperty.SHORTCUT_ACTIONS).includes(ActionsName.ShowLayerConfigurationMenu);
    if (isShortcutAction) {
      // This is not null only when the action is executed from the legend (therefore the legend is visible)
      // It will otherwise be null when the action is executed from the d-layer-action-toolbar
      const legendFlyoutParent = button.closest('.d-map-legend');
      const flyoutParent = legendFlyoutParent || document.querySelector('.d-layer-action-toolbar');

      openMenu(
        event,
        content,
        {
          styleClass: 'd-show-configuration-menu',
          target: button,
          displayArrow: false,
          position: {
            constrainHeight: true
          },
          flyoutParent,
          onClose: () => { this.active = false; }
        }
      );

      return getTemplateActions(this.layer, this.#map)
        .then(templateActions => {
          const templateActionButtons = templateActions.map(actionToButton);
          templatesContainer.innerHTML = '';
          templatesContainer.append(...templateActionButtons);
        })
        .catch(error => {
          templatesContainer.innerHTML = '';
          templatesContainer.append(
            h('p.d-show-configuration-menu--loading-error',
              h('span.d-show-configuration-menu--loading-error-prefix', i18n('common:error')),
              i18n('templates.loading.error.message')));
          logger.error(`Error loading layer templates for layer with id=${this.#layerId}`, error);
        });
    }
    return Promise.resolve();
  }

}

export function getTemplateActions (layer, map) {
  return getLayerTemplates(layer.get(LayerProperty.ID), context(map).mapScale).then(templates => {
    return templates.map(template => new ApplyLayerTemplate(
      layer,
      template,
      {
        label: template.printName,
        map
      }
    ));
  });
}

export function createShowLayerConfigurationPanelAction (layer, map) {
  return new ShowLayerConfigurationPanel(layer, {
    icon: null,
    map,
    label: i18n('mapconfig.showConfigurationMenuButton.openConfigPanel')
  });
}

function actionToButton (action) {
  return h('button.button.button-borderless.ellipsis.d-show-configuration-menu--template-button',
    {
      type: 'button',
      title: action.label,
      onclick: () => action.execute()
    },
    [
      action.icon,
      action.label
    ]);
}
